"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { apiFetch } from "@/src/lib/api-fetch";
import type { CanonicalGenerationStatus } from "@/lib/admin/course-details";

export function useCanonicalStatus({
  id,
  courseId,
}: {
  id: string;
  courseId: number | null;
}) {
  const [canonicalStatus, setCanonicalStatus] = useState<CanonicalGenerationStatus | null>(null);
  const [isPollingRequested, setIsPollingRequested] = useState(false);
  const canonicalStatusFetchAbortRef = useRef<AbortController | null>(null);

  const fetchCanonicalStatus = useCallback(async () => {
    const targetId = courseId != null ? String(courseId) : id;
    if (!targetId) return;

    canonicalStatusFetchAbortRef.current?.abort();
    const controller = new AbortController();
    canonicalStatusFetchAbortRef.current = controller;

    try {
      const res = await apiFetch(`/api/admin/courses/${targetId}`, {
        signal: controller.signal,
      });
      if (res.ok) {
        const data = await res.json();
        setCanonicalStatus(data as unknown as CanonicalGenerationStatus);
      }
    } catch (err) {
      if (controller.signal.aborted) return;
      if (err instanceof Error && err.name === "AbortError") return;
      console.error("Failed to fetch canonical status:", err);
    }
  }, [id, courseId]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      void fetchCanonicalStatus();
    }, 0);
    return () => clearTimeout(timeoutId);
  }, [fetchCanonicalStatus]);

  useEffect(() => {
    return () => {
      canonicalStatusFetchAbortRef.current?.abort();
    };
  }, []);

  const autoPollingRequested =
    Boolean(canonicalStatus) &&
    !Boolean(canonicalStatus?.is_complete) &&
    (canonicalStatus?.total_lessons ?? 0) > 0;
  const isPollingCanonicalStatus =
    (isPollingRequested || autoPollingRequested) && Boolean(courseId) && !Boolean(canonicalStatus?.is_complete);

  useEffect(() => {
    if (!isPollingCanonicalStatus || !courseId) return;
    const interval = setInterval(() => {
      void fetchCanonicalStatus();
    }, 2500);
    return () => clearInterval(interval);
  }, [isPollingCanonicalStatus, fetchCanonicalStatus, courseId]);

  return {
    canonicalStatus,
    setCanonicalStatus,
    isPollingCanonicalStatus,
    setIsPollingCanonicalStatus: setIsPollingRequested,
    fetchCanonicalStatus,
  };
}
